// frontend/src/pages/hr/Schedules.jsx
// Schedule Management Page - SRS HR Module
// HR Manager manages employee shift schedules by department

import React, { useState, useEffect, useCallback } from 'react';
import {
  Calendar, Plus, Search, Edit2, Trash2,
  X, Check, AlertCircle, Clock, Users, Filter
} from 'lucide-react';
import { api } from '../../services/api';

// ─── Shift type badge styles ───────────────────────────────────────────────
const SHIFT_STYLES = {
  morning:  { bg: 'bg-yellow-50', text: 'text-yellow-700', label: 'Morning'  },
  evening:  { bg: 'bg-orange-50', text: 'text-orange-700', label: 'Evening'  },
  night:    { bg: 'bg-indigo-50', text: 'text-indigo-700', label: 'Night'    },
  flexible: { bg: 'bg-green-50',  text: 'text-green-700',  label: 'Flexible' },
  custom:   { bg: 'bg-gray-50',   text: 'text-gray-700',   label: 'Custom'   },
};

const getShiftStyle = (type) => SHIFT_STYLES[type?.toLowerCase()] || SHIFT_STYLES.custom;

// ─── Main Component ────────────────────────────────────────────────────────
const Schedules = () => {
  const [schedules, setSchedules]     = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [successMsg, setSuccessMsg]   = useState('');
  const [searchTerm, setSearchTerm]   = useState('');
  const [filterShift, setFilterShift] = useState('');
  const [showModal, setShowModal]     = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [deleteConfirm, setDeleteConfirm]     = useState(null);
  const [saving, setSaving]           = useState(false);
  const [departments, setDepartments] = useState([]);

  // Form state — matches backend ScheduleCreate model exactly
  const [form, setForm] = useState({
    schedule_name: '',
    department: '',
    shift_type: 'morning',
    start_time: '09:00',
    end_time: '17:00',
    grace_time: 10,
    required_hours: 8.0,
  });
  const [formErrors, setFormErrors] = useState({});

  // ─── Fetch all schedules ───────────────────────────────────────────────
  const fetchSchedules = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.get('/api/schedules/');
      const list = Array.isArray(data) ? data : (data?.data || data?.schedules || []);
      setSchedules(list);
    } catch (err) {
      setError(err.message || 'Failed to load schedules');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
  fetchSchedules();
  // Load departments for dropdown
 api.get('/api/departments').then((data) => {
  const list = Array.isArray(data) ? data : (data?.data || data?.departments || []);
  setDepartments(list);
}).catch(() => {});
}, [fetchSchedules]);

  // ─── Auto hide success message after 3 seconds ─────────────────────────
  useEffect(() => {
    if (successMsg) {
      const t = setTimeout(() => setSuccessMsg(''), 3000);
      return () => clearTimeout(t);
    }
  }, [successMsg]);

  // ─── Reset form to defaults ────────────────────────────────────────────
  const defaultForm = () => ({
    schedule_name: '',
    department: '',
    shift_type: 'morning',
    start_time: '09:00',
    end_time: '17:00',
    grace_time: 10,
    required_hours: 8.0,
  });

  // ─── Open modal for adding new schedule ───────────────────────────────
  const openAddModal = () => {
    setEditingSchedule(null);
    setForm(defaultForm());
    setFormErrors({});
    setShowModal(true);
  };

  // ─── Open modal for editing existing schedule ──────────────────────────
  const openEditModal = (schedule) => {
    setEditingSchedule(schedule);
    setForm({
      schedule_name: schedule.schedule_name || '',
      department:    schedule.department    || '',
      shift_type:    schedule.shift_type    || 'morning',
      start_time:    schedule.start_time    || '09:00',
      end_time:      schedule.end_time      || '17:00',
      grace_time:    schedule.grace_time    || 10,
      required_hours: schedule.required_hours || 8.0,
    });
    setFormErrors({});
    setShowModal(true);
  };

  // ─── Close modal and reset ─────────────────────────────────────────────
  const closeModal = () => {
    setShowModal(false);
    setEditingSchedule(null);
    setFormErrors({});
  };

  // ─── Form validation ───────────────────────────────────────────────────
  const validateForm = () => {
    const errors = {};
    if (!form.schedule_name.trim()) errors.schedule_name = 'Schedule name is required';
    if (!form.department.trim())    errors.department    = 'Department is required';
    if (!form.start_time)           errors.start_time    = 'Start time is required';
    if (!form.end_time)             errors.end_time      = 'End time is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ─── Save schedule (create or update) ─────────────────────────────────
  const handleSave = async () => {
    if (!validateForm()) return;
    try {
      setSaving(true);
      if (editingSchedule) {
        const id = editingSchedule.id;
        await api.put(`/api/schedules/${id}`, form);
        setSuccessMsg('Schedule updated successfully');
      } else {
        await api.post('/api/schedules/', form);
        setSuccessMsg('Schedule created successfully');
      }
      closeModal();
      fetchSchedules();
    } catch (err) {
      setFormErrors({ submit: err.message || 'Failed to save. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  // ─── Delete schedule ───────────────────────────────────────────────────
  const handleDelete = async (scheduleId) => {
    try {
      await api.delete(`/api/schedules/${scheduleId}`);
      setDeleteConfirm(null);
      setSuccessMsg('Schedule deleted successfully');
      fetchSchedules();
    } catch (err) {
      setError(err.message || 'Failed to delete schedule');
      setDeleteConfirm(null);
    }
  };

  // ─── Filter schedules by search term and shift type ────────────────────
  const filtered = schedules.filter((s) => {
    const matchSearch =
      s.schedule_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.department?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchShift = filterShift ? s.shift_type === filterShift : true;
    return matchSearch && matchShift;
  });

  // ─── Render ────────────────────────────────────────────────────────────
  return (
    <div className="p-6 space-y-6">

      {/* ── Page Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Schedules</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage employee shift schedules by department
          </p>
        </div>
        <button
          onClick={openAddModal}
          className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Schedule
        </button>
      </div>

      {/* ── Success Message ── */}
      {successMsg && (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          <Check className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{successMsg}</span>
        </div>
      )}

      {/* ── Error Message ── */}
      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* ── Search + Filter Bar ── */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or department..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <select
            value={filterShift}
            onChange={(e) => setFilterShift(e.target.value)}
            className="pl-9 pr-8 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="">All Shifts</option>
            <option value="morning">Morning</option>
            <option value="evening">Evening</option>
            <option value="night">Night</option>
            <option value="flexible">Flexible</option>
            <option value="custom">Custom</option>
          </select>
        </div>
      </div>

      {/* ── Schedules Grid ── */}
      {loading ? (
        // Loading skeleton cards
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-100 p-5 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-3" />
              <div className="h-3 bg-gray-100 rounded w-1/2 mb-2" />
              <div className="h-3 bg-gray-100 rounded w-2/3" />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        // Empty state
        <div className="text-center py-16">
          <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">
            {searchTerm || filterShift ? 'No schedules found' : 'No schedules yet'}
          </p>
          {!searchTerm && !filterShift && (
            <button
              onClick={openAddModal}
              className="mt-3 text-blue-600 text-sm hover:underline"
            >
              Create your first schedule
            </button>
          )}
        </div>
      ) : (
        // Schedule cards
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((schedule) => {
            const shiftStyle = getShiftStyle(schedule.shift_type);
            const scheduleId = schedule.id;
            return (
              <div
                key={scheduleId}
                className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 hover:shadow-md transition-shadow"
              >
                {/* Card Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 text-sm truncate">
                      {schedule.schedule_name}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${shiftStyle.bg} ${shiftStyle.text}`}>
                        {shiftStyle.label}
                      </span>
                    </div>
                  </div>
                  {/* Edit and Delete buttons */}
                  <div className="flex items-center gap-1 ml-2">
                    <button
                      onClick={() => openEditModal(schedule)}
                      className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Edit schedule"
                    >
                      <Edit2 className="w-3.5 h-3.5 text-gray-500" />
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(scheduleId)}
                      className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete schedule"
                    >
                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                    </button>
                  </div>
                </div>

                {/* Department */}
                <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-2">
                  <Users className="w-3.5 h-3.5" />
                  <span>{schedule.department}</span>
                </div>

                {/* Timing */}
                <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-3">
                  <Clock className="w-3.5 h-3.5" />
                  <span>{schedule.start_time} — {schedule.end_time}</span>
                  {schedule.grace_time > 0 && (
                    <span className="text-gray-400">(+{schedule.grace_time}m grace)</span>
                  )}
                </div>

                {/* Required Hours */}
                <div className="border-t border-gray-50 pt-3 text-xs text-gray-500">
                  Required: <span className="font-medium text-gray-700">{schedule.required_hours}h/day</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          ADD / EDIT MODAL
      ═══════════════════════════════════════════════════════════════════ */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">

            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl">
              <h2 className="font-semibold text-gray-900">
                {editingSchedule ? 'Edit Schedule' : 'New Schedule'}
              </h2>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">

              {/* Submit Error */}
              {formErrors.submit && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {formErrors.submit}
                </div>
              )}

              {/* Schedule Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Schedule Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.schedule_name}
                  onChange={(e) => {
                    setForm({ ...form, schedule_name: e.target.value });
                    if (formErrors.schedule_name) setFormErrors({ ...formErrors, schedule_name: '' });
                  }}
                  placeholder="e.g. Morning Shift — Engineering"
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    formErrors.schedule_name ? 'border-red-400' : 'border-gray-200'
                  }`}
                />
                {formErrors.schedule_name && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.schedule_name}</p>
                )}
              </div>

              {/* Department — loads from registered departments */}
<div>
  <label className="block text-sm font-medium text-gray-700 mb-1">
    Department <span className="text-red-500">*</span>
  </label>
  <select
    value={form.department}
    onChange={(e) => {
      setForm({ ...form, department: e.target.value });
      if (formErrors.department) setFormErrors({ ...formErrors, department: '' });
    }}
    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
      formErrors.department ? 'border-red-400' : 'border-gray-200'
    }`}
  >
    <option value="">-- Select Department --</option>
    {departments.map((dept) => (
  <option key={dept.id} value={dept.department_name}>
    {dept.department_name}
  </option>
))}
  </select>
  {formErrors.department && (
    <p className="text-red-500 text-xs mt-1">{formErrors.department}</p>
  )}
</div>
              {/* Shift Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Shift Type
                </label>
                <select
                  value={form.shift_type}
                  onChange={(e) => setForm({ ...form, shift_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="morning">Morning</option>
                  <option value="evening">Evening</option>
                  <option value="night">Night</option>
                  <option value="flexible">Flexible</option>
                  <option value="custom">Custom</option>
                </select>
              </div>

              {/* Start Time + End Time */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Time <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="time"
                    value={form.start_time}
                    onChange={(e) => {
                      setForm({ ...form, start_time: e.target.value });
                      if (formErrors.start_time) setFormErrors({ ...formErrors, start_time: '' });
                    }}
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      formErrors.start_time ? 'border-red-400' : 'border-gray-200'
                    }`}
                  />
                  {formErrors.start_time && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.start_time}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Time <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="time"
                    value={form.end_time}
                    onChange={(e) => {
                      setForm({ ...form, end_time: e.target.value });
                      if (formErrors.end_time) setFormErrors({ ...formErrors, end_time: '' });
                    }}
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      formErrors.end_time ? 'border-red-400' : 'border-gray-200'
                    }`}
                  />
                  {formErrors.end_time && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.end_time}</p>
                  )}
                </div>
              </div>

              {/* Grace Time + Required Hours */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Grace Period (minutes)
                  </label>
                  <input
                    type="number"
                    value={form.grace_time}
                    onChange={(e) =>
                      setForm({ ...form, grace_time: parseInt(e.target.value) || 0 })
                    }
                    min={0}
                    max={60}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Arrivals within this are On Time
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Required Hours
                  </label>
                  <input
                    type="number"
                    value={form.required_hours}
                    onChange={(e) =>
                      setForm({ ...form, required_hours: parseFloat(e.target.value) || 8 })
                    }
                    min={1}
                    max={24}
                    step={0.5}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Daily hours required
                  </p>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-100">
              <button
                onClick={closeModal}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-60"
              >
                {saving && (
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                )}
                {editingSchedule ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          DELETE CONFIRMATION MODAL
      ═══════════════════════════════════════════════════════════════════ */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-50 rounded-full flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Delete Schedule?</h3>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Schedules;